import { useState, useEffect, useRef, useCallback } from 'react';
import storage from '../utils/storage';

const POLL_INTERVAL = 30000; // 30 seconds

export const useContentStorage = () => {
  const [content, setContentState] = useState([]);
  const [recordings, setRecordingsState] = useState([]);
  const [loading, setLoading] = useState(true);
  const dirty = useRef(false);
  const saving = useRef(false);

  // Shared load function used by initial load and polling
  const loadData = useCallback(async () => {
    try {
      const [contentData, recordingsData] = await Promise.all([
        storage.get('ct-content'),
        storage.get('ct-recordings')
      ]);

      if (contentData?.value) setContentState(JSON.parse(contentData.value));
      if (recordingsData?.value) setRecordingsState(JSON.parse(recordingsData.value));
    } catch (error) {
      console.error('Failed to load data:', error);
    }
  }, []);

  // Load data on mount
  useEffect(() => {
    loadData().finally(() => setLoading(false));
  }, [loadData]);

  // Poll for cross-device updates (production only)
  useEffect(() => {
    if (!import.meta.env.PROD) return;

    const refresh = () => {
      if (!dirty.current && !saving.current) loadData();
    };

    const interval = setInterval(refresh, POLL_INTERVAL);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [loadData]);

  // Wrap setters to mark data as dirty (user-modified)
  const setContent = useCallback((val) => {
    dirty.current = true;
    setContentState(val);
  }, []);

  const setRecordings = useCallback((val) => {
    dirty.current = true;
    setRecordingsState(val);
  }, []);

  // Save data only after user-initiated changes
  useEffect(() => {
    if (!loading && dirty.current) {
      dirty.current = false;
      saving.current = true;

      const saveData = async () => {
        try {
          await Promise.all([
            storage.set('ct-content', JSON.stringify(content)),
            storage.set('ct-recordings', JSON.stringify(recordings))
          ]);
        } catch (error) {
          console.error('Failed to save data:', error);
        } finally {
          saving.current = false;
        }
      };

      saveData();
    }
  }, [content, recordings, loading]);

  return {
    content,
    setContent,
    recordings,
    setRecordings,
    loading
  };
};
